import React from 'react';
import { useLocale } from '../contexts/LocaleContext';
import {
  ENTITY_RELEVANCE_SEGMENT_COUNT,
  clampEntityRelevance,
  entityRelevanceLevelLabelKey,
  getEntityRelevanceFilledSegments,
  getEntityRelevanceLevel,
} from '../utils/entity-relevance';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { cn } from '../lib/utils';

interface EntityRelevanceDisplayProps {
  value?: number | null;
  size?: 'compact' | 'detailed';
  className?: string;
  showTooltip?: boolean;
}

export const EntityRelevanceDisplay: React.FC<EntityRelevanceDisplayProps> = ({
  value,
  size = 'compact',
  className,
  showTooltip = true,
}) => {
  const { t } = useLocale();
  const relevance = clampEntityRelevance(value ?? undefined);
  const level = getEntityRelevanceLevel(relevance);
  const filled = getEntityRelevanceFilledSegments(relevance);
  const label = t(entityRelevanceLevelLabelKey(level));
  const tooltip = t('entity_relevance_tooltip').replace('{level}', label);

  const meter = (
    <div
      className={cn('inline-flex items-center gap-2', className)}
      aria-label={tooltip}
    >
      <div className="flex items-end gap-0.5" aria-hidden="true">
        {Array.from({ length: ENTITY_RELEVANCE_SEGMENT_COUNT }, (_, index) => {
          const isFilled = index < filled;
          const heightClass =
            index === 0
              ? 'h-1.5'
              : index === 1
                ? 'h-2'
                : index === 2
                  ? 'h-2.5'
                  : index === 3
                    ? 'h-3'
                    : 'h-3.5';
          return (
            <span
              key={index}
              className={cn(
                'w-1 rounded-sm',
                heightClass,
                isFilled ? 'bg-[var(--scribane-green)]' : 'bg-muted-foreground/25'
              )}
            />
          );
        })}
      </div>
      <span
        className={cn(
          'text-muted-foreground',
          size === 'detailed' ? 'text-sm font-medium text-foreground' : 'text-xs'
        )}
      >
        {label}
      </span>
    </div>
  );

  if (!showTooltip) {
    return <span className="inline-flex max-w-full">{meter}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex max-w-full">{meter}</span>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltip}</TooltipContent>
    </Tooltip>
  );
};
